import ObjectConfigApi from '../api/ObjectConfigApi';
import DataApi from '../api/DataApi';
import { getFieldDataType, getFieldByName } from './conditionBuilderUtils';
import {
  isDateLiteral,
  isDateOrDateTimeFieldType,
} from './dateLiteralUtils';

export const FILTER_VALUE_TYPE_STATIC = 'static';
export const FILTER_VALUE_TYPE_FIELD = 'field';
/** Compare to logged-in / acting user via {{LoggedUser.field}}. */
export const FILTER_VALUE_TYPE_LOGGED_USER = 'loggedUser';
/** @deprecated use FILTER_VALUE_TYPE_LOGGED_USER */
export const FILTER_VALUE_TYPE_CURRENT_USER = FILTER_VALUE_TYPE_LOGGED_USER;
/** Date/datetime filters: TODAY, LAST_N_DAYS:7, YYYY-MM-DD (not field compare). */
export const FILTER_VALUE_TYPE_LITERAL = 'literal';

const FIELD_MERGE_PATTERN = /\{\{\s*([^}]+)\s*\}\}/;
const LOGGED_USER_REF_PATTERN = /^(?:LoggedUser|CurrentUser)\./i;

/** Fields available when comparing to the logged-in user (validation / workflow criteria). */
export const LOGGED_USER_COMPARE_FIELDS = [
  { field_name: 'id', field_label: 'User ID' },
  { field_name: 'username', field_label: 'Username' },
  { field_name: 'email', field_label: 'Email' },
  { field_name: 'phone', field_label: 'Phone' },
  { field_name: 'firstname', field_label: 'First Name' },
  { field_name: 'lastname', field_label: 'Last Name' },
  { field_name: 'role_id', field_label: 'Role ID' },
  { field_name: 'role_name', field_label: 'Role Name' },
  { field_name: 'managerid', field_label: 'Manager ID' },
];

/** @deprecated use LOGGED_USER_COMPARE_FIELDS */
export const CURRENT_USER_COMPARE_FIELDS = LOGGED_USER_COMPARE_FIELDS;

/** Matches `{{LoggedUser.` / legacy `{{CurrentUser.` while typing a field name. */
const LOGGED_USER_TYPING_RE =
  /^\{\{\s*(?:LoggedUser|CurrentUser|Curren(?:t)?User)\.?([A-Za-z0-9_]*)$/i;

/**
 * When the caret is inside an incomplete {{LoggedUser.*}} token, return replace range + partial field.
 * @returns {{ openIdx: number, partial: string, replaceStart: number, replaceEnd: number }|null}
 */
export function getLoggedUserMergeAutocomplete(text, caretIndex) {
  if (text == null) return null;
  const caret =
    caretIndex == null || Number.isNaN(Number(caretIndex))
      ? String(text).length
      : Math.max(0, Number(caretIndex));
  const before = String(text).slice(0, caret);
  const openIdx = before.lastIndexOf('{{');
  if (openIdx === -1) return null;

  const fragment = before.slice(openIdx);
  const m = fragment.match(LOGGED_USER_TYPING_RE);
  if (!m) return null;

  const afterCaret = String(text).slice(caret);
  if (afterCaret.startsWith('}}')) return null;

  return {
    openIdx,
    partial: m[1] || '',
    replaceStart: openIdx,
    replaceEnd: caret,
  };
}

export function filterLoggedUserFieldSuggestions(
  partial,
  fields = LOGGED_USER_COMPARE_FIELDS
) {
  const p = String(partial || '').toLowerCase();
  if (!p) return [...fields];
  return fields.filter((f) => {
    const name = String(f.field_name || '').toLowerCase();
    const label = String(f.field_label || '').toLowerCase();
    return name.startsWith(p) || name.includes(p) || label.includes(p);
  });
}

/**
 * Replace incomplete {{LoggedUser.*}} at caret with a full token.
 */
export function applyLoggedUserMergeCompletion(text, caretIndex, fieldName) {
  const token = encodeLoggedUserCompareValue(fieldName);
  const ctx = getLoggedUserMergeAutocomplete(text, caretIndex);
  if (!ctx) {
    const base = text != null ? String(text) : '';
    return { value: base + token, caret: base.length + token.length };
  }
  const newValue =
    String(text).slice(0, ctx.replaceStart) +
    token +
    String(text).slice(ctx.replaceEnd);
  return { value: newValue, caret: ctx.replaceStart + token.length };
}

export function encodeLoggedUserCompareValue(fieldName) {
  const fn = String(fieldName ?? '').trim();
  if (!fn) return '';
  return `{{LoggedUser.${fn}}}`;
}

export function decodeLoggedUserCompareValue(storedValue) {
  const inner = decodeFieldCompareValue(storedValue);
  const m = inner.match(/^(?:LoggedUser|CurrentUser)\.(.+)$/i);
  return m ? m[1].trim() : inner;
}

export function isLoggedUserMergeToken(storedValue) {
  if (storedValue == null || storedValue === '') return false;
  const inner = decodeFieldCompareValue(storedValue);
  return LOGGED_USER_REF_PATTERN.test(String(inner).trim());
}

/** @deprecated */
export const getCurrentUserMergeAutocomplete = getLoggedUserMergeAutocomplete;
export const filterCurrentUserFieldSuggestions = filterLoggedUserFieldSuggestions;
export const applyCurrentUserMergeCompletion = applyLoggedUserMergeCompletion;
export const encodeCurrentUserCompareValue = encodeLoggedUserCompareValue;
export const decodeCurrentUserCompareValue = decodeLoggedUserCompareValue;
export const isCurrentUserMergeToken = isLoggedUserMergeToken;

/** Store a field reference for criteria/filter compare (same as workflow merge fields). */
export function encodeFieldCompareValue(fieldName) {
  const fn = String(fieldName ?? '').trim();
  if (!fn) return '';
  return `{{${fn}}}`;
}

/** Parse `{{field_name}}` or `{{lookup.child}}` for dropdowns and editors. */
export function decodeFieldCompareValue(storedValue) {
  if (storedValue == null || storedValue === '') return '';
  const s = String(storedValue).trim();
  const m = s.match(FIELD_MERGE_PATTERN);
  if (m) return m[1].trim();
  return s;
}

export function isFieldMergeToken(storedValue) {
  if (isLoggedUserMergeToken(storedValue)) return false;
  return FIELD_MERGE_PATTERN.test(String(storedValue ?? ''));
}

/**
 * Resolve picker output to API field name (never a display label).
 * Supports same-object names and parent refs `lookup_field.child_field`.
 */
/**
 * Picker value for report filters: prefer field_config id; accept legacy {{field_name}} tokens.
 */
export function resolveReportFieldComparePickerValue(
  fields,
  storedValue,
  nameKey = 'id'
) {
  const token = decodeFieldCompareValue(storedValue);
  if (!token || !Array.isArray(fields)) return '';

  const byId = fields.find((f) => String(f.id) === String(token));
  if (byId) return String(byId.id);

  const tokenLower = token.toLowerCase();
  const byName = fields.find(
    (f) =>
      String(f.field_name || '').trim().toLowerCase() === tokenLower
  );
  if (byName) return String(byName.id);

  return resolveFieldApiNameForCompare(fields, storedValue, nameKey, 'field_label');
}

export function resolveFieldApiNameForCompare(
  fields,
  picked,
  nameKey = 'field_name',
  labelKey = 'field_label'
) {
  const token = decodeFieldCompareValue(picked);
  if (!token || !Array.isArray(fields)) return token;

  const byApi = fields.find(
    (f) => String(f[nameKey] ?? f.field_name ?? '') === token
  );
  if (byApi) {
    return String(byApi.field_name ?? byApi[nameKey] ?? token).trim();
  }

  const tokenLower = token.toLowerCase();
  const byApiCi = fields.find((f) => {
    const api = String(f.field_name ?? f[nameKey] ?? '').trim();
    return api && api.toLowerCase() === tokenLower;
  });
  if (byApiCi) {
    return String(byApiCi.field_name ?? byApiCi[nameKey] ?? token).trim();
  }

  const byLabel = fields.find((f) => {
    const lbl = String(
      f[labelKey] ?? f.field_label ?? f.field_display_name ?? f.label ?? ''
    ).trim();
    return lbl && lbl.toLowerCase() === tokenLower;
  });
  if (byLabel) {
    return String(byLabel.field_name ?? byLabel[nameKey] ?? token).trim();
  }

  return token;
}

/**
 * Infer date literal mode from stored value when value_type was not saved.
 * ISO dates (YYYY-MM-DD) are excluded — those belong to static date picker mode.
 */
function storedValueLooksLikeDateLiteral(value, dataType, operator) {
  if (!isDateOrDateTimeFieldType(dataType)) return false;
  if (value == null || String(value).trim() === '') return false;
  const s = String(value).trim();
  if (isFieldMergeToken(s) || isLoggedUserMergeToken(s)) return false;
  if (operator != null && String(operator).toLowerCase() === 'between') {
    const parts = s.split('|').map((p) => p.trim());
    const from = parts[0] || '';
    const to = parts[1] || '';
    if (!from || !to) return false;
    return isDateLiteral(from) || isDateLiteral(to);
  }
  return isDateLiteral(s);
}

/** Read valueType from a filter/criteria row (defaults to static). */
export function getFilterValueType(row) {
  if (!row || typeof row !== 'object') return FILTER_VALUE_TYPE_STATIC;
  const t = row.valueType ?? row.value_type;
  if (
    t === FILTER_VALUE_TYPE_LOGGED_USER ||
    t === 'loggedUser' ||
    t === 'logged_user' ||
    t === FILTER_VALUE_TYPE_CURRENT_USER ||
    t === 'currentUser' ||
    t === 'current_user'
  ) {
    return FILTER_VALUE_TYPE_LOGGED_USER;
  }
  if (t === FILTER_VALUE_TYPE_FIELD || t === 'field') return FILTER_VALUE_TYPE_FIELD;
  if (t === FILTER_VALUE_TYPE_LITERAL || t === 'literal' || t === 'date_literal') {
    return FILTER_VALUE_TYPE_LITERAL;
  }
  if (t === FILTER_VALUE_TYPE_STATIC || t === 'static') {
    return FILTER_VALUE_TYPE_STATIC;
  }
  if (typeof row.value === 'string' && isLoggedUserMergeToken(row.value)) {
    return FILTER_VALUE_TYPE_LOGGED_USER;
  }
  if (typeof row.value === 'string' && isFieldMergeToken(row.value)) {
    return FILTER_VALUE_TYPE_FIELD;
  }
  const dataType = row.data_type ?? row.dataType;
  if (t == null && storedValueLooksLikeDateLiteral(row.value, dataType, row.operator)) {
    return FILTER_VALUE_TYPE_LITERAL;
  }
  return FILTER_VALUE_TYPE_STATIC;
}

/** Set valueType on a loaded filter row (infers date literals from stored value). */
export function withInferredFilterValueType(row) {
  if (!row || typeof row !== 'object') return row;
  return { ...row, valueType: getFilterValueType(row) };
}

/** Normalize valueType on save/API payloads. */
export function withFilterValueTypeDefaults(row) {
  if (!row || typeof row !== 'object') return row;
  const out = { ...row };
  if (out.valueType == null && out.value_type == null) {
    out.valueType = FILTER_VALUE_TYPE_STATIC;
  } else if (out.valueType == null && out.value_type != null) {
    const vt = out.value_type;
    if (vt === 'field') out.valueType = FILTER_VALUE_TYPE_FIELD;
    else if (
      vt === 'loggedUser' ||
      vt === 'logged_user' ||
      vt === 'currentUser' ||
      vt === 'current_user'
    ) {
      out.valueType = FILTER_VALUE_TYPE_LOGGED_USER;
    } else if (vt === 'literal' || vt === 'date_literal') {
      out.valueType = FILTER_VALUE_TYPE_LITERAL;
    } else out.valueType = FILTER_VALUE_TYPE_STATIC;
  }
  delete out.value_type;
  return out;
}

const COMPATIBLE_TYPE_GROUPS = [
  ['number', 'integer', 'decimal', 'currency', 'percent', 'percentage', 'double', 'float', 'int', 'numeric', 'real', 'bigint', 'smallint', 'money', 'autonumber'],
  ['date', 'datetime'],
  ['time'],
  ['boolean', 'checkbox'],
  ['picklist', 'multipicklist'],
  ['text', 'string', 'email', 'phone', 'url', 'textarea'],
  ['lookup', 'reference', 'uuid'],
];

function typeGroup(dataType) {
  const t = String(dataType || 'text').toLowerCase();
  for (let i = 0; i < COMPATIBLE_TYPE_GROUPS.length; i++) {
    if (COMPATIBLE_TYPE_GROUPS[i].includes(t)) return i;
  }
  return -1;
}

/** Whether a right-hand field can be compared to the selected left-hand field. */
export function areFilterValueTypesCompatible(leftField, rightField) {
  if (!leftField || !rightField) return false;
  const leftType = getFieldDataType(leftField);
  const rightType = getFieldDataType(rightField);
  if (leftType === rightType) return true;
  const lg = typeGroup(leftType);
  const rg = typeGroup(rightType);
  return lg >= 0 && lg === rg;
}

/**
 * Build dropdown options for "compare to field": current object fields + parent lookup fields (invoice.amount).
 * @returns {Promise<{ baseFields: Array, valueFields: Array }>}
 */
export async function buildValueFieldsForObject(objectName) {
  if (!objectName) {
    return { baseFields: [], valueFields: [] };
  }

  const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
  const payload = response?.data ?? response;
  const baseFields =
    payload?.fields ||
    payload?.data?.fields ||
    (response?.success && response?.data?.fields ? response.data.fields : []) ||
    [];

  const lookupFields = baseFields.filter(
    (f) => f.data_type === 'lookup' && (f.reference_object || f.referenceObject)
  );
  const parentObjectNames = [
    ...new Set(
      lookupFields
        .map((f) => f.reference_object || f.referenceObject)
        .filter(Boolean)
    ),
  ];

  const parentConfigsByObject = {};
  if (parentObjectNames.length > 0) {
    try {
      const dataApi = new DataApi('objectconfig');
      const configsResponse = await dataApi.post('/fetchconfigs', {
        objectNames: parentObjectNames,
      });
      const configs = configsResponse?.data?.data || configsResponse?.data || [];
      if (Array.isArray(configs)) {
        configs.forEach((cfg) => {
          if (cfg?.object_name) {
            parentConfigsByObject[cfg.object_name] = cfg.fields || [];
          }
        });
      }
    } catch (e) {
      console.warn('[filterFieldValueUtils] Failed to load parent object fields:', e);
    }
  }

  const valueFields = [...baseFields];

  for (const lookup of lookupFields) {
    const refObj = lookup.reference_object || lookup.referenceObject;
    const parentFields = parentConfigsByObject[refObj] || [];
    const lookupLabel = lookup.field_label || lookup.field_name;
    for (const pf of parentFields) {
      if (pf.data_type === 'calculated') continue;
      const childName = pf.field_name;
      if (!childName) continue;
      const compositeName = `${lookup.field_name}.${childName}`;
      valueFields.push({
        ...pf,
        field_name: compositeName,
        field_label: `${lookupLabel} › ${pf.field_label || childName}`,
        _isParentField: true,
        _parentLookupField: lookup.field_name,
        _parentObject: refObj,
      });
    }
  }

  return { baseFields, valueFields };
}

/** @deprecated Legacy report prefix; prefer `{{field_config_id}}`. */
export const REPORT_FIELD_VALUE_PREFIX = 'field:';

/** Report filters: compare target field config id as `{{uuid}}`. */
export function encodeReportFieldCompareValue(fieldConfigId) {
  return encodeFieldCompareValue(fieldConfigId);
}

/** ConditionRow state from stored report filter value. */
export function parseReportFilterValueFields(rawValue, rawValueType, options = {}) {
  const { dataType, operator } = options;
  const s = rawValue != null ? String(rawValue) : '';
  const explicitLiteral =
    rawValueType === FILTER_VALUE_TYPE_LITERAL ||
    rawValueType === 'literal' ||
    rawValueType === 'date_literal';
  const explicitStatic =
    rawValueType === FILTER_VALUE_TYPE_STATIC || rawValueType === 'static';
  const inferredLiteral =
    !explicitLiteral &&
    !explicitStatic &&
    rawValueType == null &&
    storedValueLooksLikeDateLiteral(rawValue, dataType, operator);

  if (explicitLiteral || inferredLiteral) {
    return {
      valueType: FILTER_VALUE_TYPE_LITERAL,
      value: rawValue ?? '',
    };
  }
  if (rawValueType === FILTER_VALUE_TYPE_FIELD || isFieldMergeToken(s)) {
    return {
      valueType: FILTER_VALUE_TYPE_FIELD,
      value: decodeFieldCompareValue(s),
    };
  }
  if (s.startsWith(REPORT_FIELD_VALUE_PREFIX)) {
    return {
      valueType: FILTER_VALUE_TYPE_FIELD,
      value: s.slice(REPORT_FIELD_VALUE_PREFIX.length),
    };
  }
  return { valueType: FILTER_VALUE_TYPE_STATIC, value: rawValue ?? '' };
}

export function filterValueFieldsForLeftField(valueFields, leftFieldName, nameKey = 'field_name') {
  if (!leftFieldName || !Array.isArray(valueFields)) return valueFields || [];
  const leftField = getFieldByName(valueFields, leftFieldName, { nameKey });
  if (!leftField) return valueFields;
  return valueFields.filter((f) => {
    const fn = f[nameKey] || f.name;
    if (fn === leftFieldName) return false;
    return areFilterValueTypesCompatible(leftField, f);
  });
}
